import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MailCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";

const SYMBOL = `${process.env.PUBLIC_URL}/amanah-symbol.png`;

export default function ForgotPasswordPage() {
  usePageMeta("Reset password — Amanah Promotions");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-6 py-16">
        <Link to="/" className="mb-10 flex items-center gap-3" data-testid="forgot-home-link">
          <img src={SYMBOL} alt="Amanah" className="h-10 w-auto" />
          <span className="font-display text-lg font-medium text-navy">Amanah Promotions</span>
        </Link>

        <div className="border border-line bg-white p-8 sm:p-10">
          {sent ? (
            <div data-testid="forgot-neutral-message">
              <MailCheck className="h-9 w-9 text-brand-green" />
              <h1 className="mt-4 font-display text-2xl font-medium text-navy">Check your inbox</h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-body">
                If that email address belongs to an administrator account, a password reset link has
                been sent. The link expires in 30 minutes and can be used once.
              </p>
              <Link
                to="/site-settings/login"
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand-green hover:text-brand-greenDeep"
                data-testid="forgot-back-to-login"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-medium text-navy sm:text-3xl">Forgot password</h1>
              <p className="mt-2 text-sm text-slate-body">
                Enter your administrator email address and we&apos;ll send you a link to set a new password.
              </p>
              <form onSubmit={onSubmit} className="mt-8 space-y-5" noValidate>
                <div>
                  <label htmlFor="forgot-email" className="mb-2 block text-sm font-medium text-navy">
                    Email
                  </label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-none border-line bg-white text-navy focus-visible:ring-2 focus-visible:ring-brand-green md:text-base"
                    autoComplete="username"
                    data-testid="forgot-email"
                    required
                  />
                </div>
                {error && (
                  <p role="alert" className="text-sm font-medium text-red-600" data-testid="forgot-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center gap-2 bg-brand-green px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-brand-greenDeep disabled:opacity-60"
                  data-testid="forgot-submit"
                >
                  {loading ? "Sending…" : "Send reset link"}
                  {!loading && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>
            </>
          )}
        </div>

        <Link to="/site-settings/login" className="mt-8 text-sm text-slate-muted hover:text-brand-green">
          ← Back to sign in
        </Link>
      </div>
    </div>
  );
}
